<template>
  <div class="details-screen">
    <!-- Header -->
    <div class="screen-header">
      <button class="screen-header__back" @click="goBack">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 class="screen-header__title">
        {{ summaryTitle || t("summary_details") }}
      </h1>
      <div v-if="isCompleted" class="screen-header__actions">
        <button class="screen-header__btn" @click="copyContent" title="Copy">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="details-content">
      <!-- Status Section -->
      <section class="details-section">
        <!-- Loading -->
        <div v-if="loading" class="status-card status-card--loading">
          <div class="spinner"></div>
          <span>{{ t("loading") }}</span>
        </div>

        <!-- Processing -->
        <div
          v-else-if="isProcessing"
          class="status-card status-card--processing"
        >
          <div class="processing-animation">
            <div class="processing-animation__ring"></div>
            <div class="processing-animation__icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
          </div>
          <h4>{{ t("processing") }}</h4>
          <p>{{ t("wait_or_retry") }}</p>
          <div class="progress-bar">
            <div
              class="progress-bar__fill"
              :style="{ width: `${progressPercent}%` }"
            ></div>
          </div>
          <span class="progress-text">{{ progressPercent }}%</span>
        </div>

        <!-- Failed -->
        <div v-else-if="isFailed" class="status-card status-card--failed">
          <div class="status-icon status-icon--error">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h4>{{ t("generation_failed") }}</h4>
          <p>{{ errorMessage || t("submission_failed") }}</p>
          <button
            class="btn btn--primary"
            @click="handleRetry"
            :disabled="retrying"
          >
            {{ retrying ? t("loading") : t("retry") }}
          </button>
        </div>

        <!-- Timeout -->
        <div v-else-if="isTimeout" class="status-card status-card--timeout">
          <div class="status-icon status-icon--warning">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
          </div>
          <h4>{{ t("taking_longer") }}</h4>
          <p>{{ t("wait_or_retry") }}</p>
          <button class="btn btn--secondary" @click="handleRetry">
            {{ t("retry") }}
          </button>
        </div>

        <!-- Completed - Show Summary Content -->
        <div
          v-else-if="isCompleted && summaryContent"
          class="summary-content-card"
        >
          <LatexRenderer :content="summaryText" :is-rtl="isRtl" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { usePolling } from "../services/pollingService";
import { OperationStatus } from "../types";
import { useI18n } from "../i18n";
import LatexRenderer from "../components/common/LatexRenderer.vue";

export default {
  name: "SummaryDetailsScreen",
  components: {
    LatexRenderer,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const miaStore = useMiaStore();
    const polling = usePolling();
    const { t, isRtl } = useI18n();

    const summaryId = ref(null);
    const summaryTitle = ref("");
    const summaryContent = ref(null);

    const loading = ref(true);
    const status = ref(null);
    const errorMessage = ref(null);
    const retrying = ref(false);
    const isTimeout = ref(false);
    const progressPercent = ref(0);

    const isProcessing = computed(
      () =>
        status.value === OperationStatus.PENDING ||
        status.value === OperationStatus.PROCESSING
    );
    const isCompleted = computed(
      () => status.value === OperationStatus.COMPLETED
    );
    const isFailed = computed(() => status.value === OperationStatus.FAILED);

    const formattedContent = computed(() => {
      if (!summaryContent.value) return "";
      let content = summaryContent.value;
      if (typeof content === "object") {
        content =
          content.content || content.result || JSON.stringify(content, null, 2);
      }
      // Basic markdown conversion
      return content
        .replace(/\n/g, "<br>")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(/^- (.*)/gm, "• $1");
    });

    const summaryText = computed(() => {
      if (!summaryContent.value) return "";

      const data = summaryContent.value;

      // Handle structured content with steps array
      if (data.steps && Array.isArray(data.steps)) {
        let content = "";
        if (data.title) {
          content += `## ${data.title}\n\n`;
        }
        content += data.steps.join("\n\n");
        return content;
      }

      // Handle simple content
      let content = data.content || data.result || data;
      if (typeof content === "object") {
        if (content.steps) {
          return content.steps.join("\n\n");
        }
        content = JSON.stringify(content, null, 2);
      }
      return typeof content === "string" ? content : String(content);
    });

    const fetchStatus = async () => {
      if (!summaryId.value) return;

      const result = await miaStore.getSummaryStatus(summaryId.value);

      if (result.success) {
        const data = result.data;
        status.value = data.status;
        summaryTitle.value = data.title || summaryTitle.value;
        summaryContent.value = data.content || data.result || data.output;
        errorMessage.value = data.error_message || data.errorMessage;

        if (data.progress !== undefined) {
          progressPercent.value = Math.round(data.progress * 100);
        } else if (isProcessing.value) {
          progressPercent.value = Math.min(progressPercent.value + 5, 90);
        }
      }
    };

    const startStatusPolling = () => {
      polling.start({
        id: `summary-${summaryId.value}`,
        endpoint: `ai/outputs/${summaryId.value}/status`,
        onUpdate: (data) => {
          status.value = data.status;
          summaryTitle.value = data.title || summaryTitle.value;
          summaryContent.value = data.content || data.result || data.output;
          errorMessage.value = data.error_message || data.errorMessage;

          if (data.progress !== undefined) {
            progressPercent.value = Math.round(data.progress * 100);
          } else if (isProcessing.value) {
            progressPercent.value = Math.min(progressPercent.value + 5, 90);
          }
        },
        onComplete: (data) => {
          status.value = OperationStatus.COMPLETED;
          summaryContent.value = data.content || data.result || data.output;
          progressPercent.value = 100;
        },
        onFailed: (data) => {
          status.value = OperationStatus.FAILED;
          errorMessage.value =
            data.error_message || data.errorMessage || "Processing failed";
        },
        onTimeout: () => {
          isTimeout.value = true;
        },
        checkComplete: (data) => data.status === OperationStatus.COMPLETED,
        checkFailed: (data) => data.status === OperationStatus.FAILED,
      });
    };

    const handleRetry = async () => {
      if (retrying.value) return;

      retrying.value = true;
      isTimeout.value = false;
      status.value = OperationStatus.PROCESSING;
      progressPercent.value = 0;

      const result = await miaStore.retrySummary(summaryId.value);

      if (result.success) {
        startStatusPolling();
      } else {
        status.value = OperationStatus.FAILED;
        errorMessage.value = result.error || "Retry failed";
      }

      retrying.value = false;
    };

    const copyContent = async () => {
      if (!summaryContent.value) return;

      let content = summaryContent.value;
      if (typeof content === "object") {
        content =
          content.content || content.result || JSON.stringify(content, null, 2);
      }

      try {
        await navigator.clipboard.writeText(content);
        alert("Content copied to clipboard!");
      } catch (e) {
        console.error("Failed to copy:", e);
      }
    };

    const goBack = () => {
      router.push("/mia/summaries");
    };

    onMounted(async () => {
      summaryId.value = route.query.id;

      if (!summaryId.value) {
        router.push("/mia/summaries");
        return;
      }

      await fetchStatus();
      loading.value = false;

      if (isProcessing.value) {
        startStatusPolling();
      }
    });

    onUnmounted(() => {
      polling.cleanup();
    });

    return {
      t,
      summaryId,
      summaryTitle,
      summaryContent,
      loading,
      status,
      errorMessage,
      retrying,
      isTimeout,
      progressPercent,
      isProcessing,
      isCompleted,
      isFailed,
      formattedContent,
      summaryText,
      isRtl,
      handleRetry,
      copyContent,
      goBack,
    };
  },
};
</script>

<style scoped>
.details-screen {
  min-height: 100vh;
  background: #f9fafb;
}

.screen-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
}

.screen-header__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: #374151;
  transition: background 0.2s ease;
}

.screen-header__back:hover {
  background: #f3f4f6;
}

.screen-header__title {
  flex: 1;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.screen-header__actions {
  display: flex;
  gap: 8px;
}

.screen-header__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: #374151;
  transition: all 0.2s ease;
}

.screen-header__btn:hover {
  background: #e5e7eb;
  color: #6366f1;
}

.details-content {
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
}

.details-section {
  margin-bottom: 24px;
}

.status-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 48px 32px;
  text-align: center;
}

.status-card h4 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 16px 0 8px;
}

.status-card p {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
}

.status-card--loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6b7280;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.processing-animation {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.processing-animation__ring {
  position: absolute;
  inset: 0;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.processing-animation__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
}

.progress-bar {
  width: 100%;
  max-width: 300px;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  margin: 0 auto 12px;
  overflow: hidden;
}

.progress-bar__fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #6366f1;
}

.status-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.status-icon--error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-icon--warning {
  background: rgba(249, 115, 22, 0.1);
  color: #f97316;
}

.summary-content-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 24px;
}

.summary-content {
  font-size: 15px;
  color: #374151;
  line-height: 1.8;
}

.btn {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn--primary {
  background: #6366f1;
  color: white;
  border: none;
}

.btn--primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn--primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn--secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn--secondary:hover {
  background: #f3f4f6;
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .details-screen,
[data-theme="dark"] .details-screen,
.dark .details-screen {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .screen-header,
[data-theme="dark"] .screen-header,
.dark .screen-header {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .screen-header__back,
[data-theme="dark"] .screen-header__back,
.dark .screen-header__back {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-header__back:hover,
[data-theme="dark"] .screen-header__back:hover,
.dark .screen-header__back:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .screen-header__title,
[data-theme="dark"] .screen-header__title,
.dark .screen-header__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .screen-header__btn,
[data-theme="dark"] .screen-header__btn,
.dark .screen-header__btn {
  background: #334155;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-header__btn:hover,
[data-theme="dark"] .screen-header__btn:hover,
.dark .screen-header__btn:hover {
  background: rgba(0, 196, 68, 0.15);
  color: #00c444;
}

[data-bs-theme="dark"] .status-card,
[data-theme="dark"] .status-card,
.dark .status-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .status-card h4,
[data-theme="dark"] .status-card h4,
.dark .status-card h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .status-card p,
[data-theme="dark"] .status-card p,
.dark .status-card p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .status-card--loading,
[data-theme="dark"] .status-card--loading,
.dark .status-card--loading {
  color: #94a3b8;
}

[data-bs-theme="dark"] .spinner,
[data-theme="dark"] .spinner,
.dark .spinner {
  border-color: #475569;
  border-top-color: #00c444;
}

[data-bs-theme="dark"] .processing-animation__ring,
[data-theme="dark"] .processing-animation__ring,
.dark .processing-animation__ring {
  border-color: #475569;
  border-top-color: #00c444;
}

[data-bs-theme="dark"] .processing-animation__icon,
[data-theme="dark"] .processing-animation__icon,
.dark .processing-animation__icon {
  color: #00c444;
}

[data-bs-theme="dark"] .progress-bar,
[data-theme="dark"] .progress-bar,
.dark .progress-bar {
  background: #475569;
}

[data-bs-theme="dark"] .progress-bar__fill,
[data-theme="dark"] .progress-bar__fill,
.dark .progress-bar__fill {
  background: linear-gradient(90deg, #00c444, #00a83b);
}

[data-bs-theme="dark"] .progress-text,
[data-theme="dark"] .progress-text,
.dark .progress-text {
  color: #00c444;
}

[data-bs-theme="dark"] .summary-content-card,
[data-theme="dark"] .summary-content-card,
.dark .summary-content-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .summary-content,
[data-theme="dark"] .summary-content,
.dark .summary-content {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .btn--primary,
[data-theme="dark"] .btn--primary,
.dark .btn--primary {
  background: #00c444;
}

[data-bs-theme="dark"] .btn--primary:hover:not(:disabled),
[data-theme="dark"] .btn--primary:hover:not(:disabled),
.dark .btn--primary:hover:not(:disabled) {
  background: #00a83b;
}

[data-bs-theme="dark"] .btn--secondary,
[data-theme="dark"] .btn--secondary,
.dark .btn--secondary {
  background: #334155;
  color: #e2e8f0;
  border-color: #475569;
}

[data-bs-theme="dark"] .btn--secondary:hover,
[data-theme="dark"] .btn--secondary:hover,
.dark .btn--secondary:hover {
  background: #475569;
}
</style>
